// Shared helpers to download an Inpix template, optionally composited with a frame overlay.
// Used by the public maker/view screen (and mirrors the admin preview download logic).

export const REQ_W = 1080, REQ_H = 1920;

// Download a stored file (blob first, fall back to opening in a new tab).
export const downloadFile = async (url, filename) => {
    if (!url) return;
    try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) throw new Error('network');
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl; a.download = filename || 'status';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objUrl);
    } catch (e) {
        const a = document.createElement('a');
        a.href = url; a.target = '_blank'; a.rel = 'noopener'; a.click();
    }
};

// Load an image with CORS so it can be drawn to a canvas without tainting it.
export const loadCorsImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

// Load an image; set crossOrigin only for remote URLs (local blob URLs don't need it).
const loadImage = (src, cors) => new Promise((resolve, reject) => {
    const img = new Image();
    if (cors) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

// Draw an image into a box with object-fit: cover behaviour.
const drawImageCover = (ctx, img, dx, dy, dW, dH) => {
    const ir = (img.naturalWidth || 1) / (img.naturalHeight || 1);
    const dr = dW / dH;
    let sx, sy, sw, sh;
    if (ir > dr) { sh = img.naturalHeight; sw = sh * dr; sx = (img.naturalWidth - sw) / 2; sy = 0; }
    else { sw = img.naturalWidth; sh = sw / dr; sx = 0; sy = (img.naturalHeight - sh) / 2; }
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dW, dH);
};

const roundRectPath = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
};

// Draw the user photo as a free-sized (independent w/h), rounded (radius 0 = rect … 0.5 = ellipse),
// positioned + rotated layer with an optional border.
// photo = { x, y, w, h, radius, rotation, borderColor, borderWidth } — all dimensions are fractions.
const drawPhotoLayer = (ctx, img, photo, W, H) => {
    const wpx = (photo.w ?? 0.34) * W;
    const hpx = (photo.h ?? 0.19) * H;
    const cx = (photo.x ?? 0.5) * W;
    const cy = (photo.y ?? 0.6) * H;
    const rot = ((photo.rotation || 0) * Math.PI) / 180;
    const x = -wpx / 2, y = -hpx / 2;
    const radiusFrac = Math.max(0, Math.min(photo.radius ?? 0.5, 0.5));
    const minSide = Math.min(wpx, hpx);
    const r = radiusFrac * minSide;

    ctx.save();
    ctx.translate(cx, cy);
    if (rot) ctx.rotate(rot);

    // clipped image (ellipse when fully round, else rounded rect)
    ctx.save();
    if (radiusFrac >= 0.5) { ctx.beginPath(); ctx.ellipse(0, 0, wpx / 2, hpx / 2, 0, 0, Math.PI * 2); ctx.clip(); }
    else { roundRectPath(ctx, x, y, wpx, hpx, r); ctx.clip(); }
    drawImageCover(ctx, img, x, y, wpx, hpx);
    ctx.restore();

    // optional border (drawn inside the box edge)
    const bw = (photo.borderWidth || 0) * minSide;
    if (bw > 0 && photo.borderColor) {
        ctx.strokeStyle = photo.borderColor;
        ctx.lineWidth = bw;
        if (radiusFrac >= 0.5) { ctx.beginPath(); ctx.ellipse(0, 0, (wpx - bw) / 2, (hpx - bw) / 2, 0, 0, Math.PI * 2); ctx.stroke(); }
        else { roundRectPath(ctx, x + bw / 2, y + bw / 2, wpx - bw, hpx - bw, Math.max(0, r - bw / 2)); ctx.stroke(); }
    }
    ctx.restore();
};

// Luminance check so the highlight pill contrasts the text color.
const textIsLight = (hex) => {
    try {
        const c = (hex || '#ffffff').replace('#', '');
        const n = c.length === 3 ? c.split('').map((x) => x + x).join('') : c;
        const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
        return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
    } catch (e) { return true; }
};

// Draw a text layer. t = { content, x, y, size, color, font, bold, italic, stroke, bg, rotation }
// x,y = center fraction; size = font px / canvas width. Supports multi-line via "\n".
const drawTextLayer = (ctx, t, W, H) => {
    const content = String(t.content || '');
    if (!content) return;
    const fontPx = (t.size || 0.06) * W;
    const lines = content.split('\n');
    const lh = fontPx * 1.15;

    ctx.save();
    ctx.translate((t.x ?? 0.5) * W, (t.y ?? 0.4) * H);
    if (t.rotation) ctx.rotate((t.rotation * Math.PI) / 180);
    ctx.font = `${t.italic ? 'italic ' : ''}${t.bold ? 'bold ' : ''}${fontPx}px ${t.font || 'Arial, sans-serif'}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // optional highlight background pill (contrasts the text color)
    if (t.bg) {
        let maxW = 0;
        lines.forEach((ln) => { maxW = Math.max(maxW, ctx.measureText(ln).width); });
        const padX = fontPx * 0.36, padY = fontPx * 0.2;
        const totalH = lines.length * lh;
        ctx.save();
        ctx.fillStyle = textIsLight(t.color) ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)';
        roundRectPath(ctx, -maxW / 2 - padX, -totalH / 2 - padY, maxW + padX * 2, totalH + padY * 2, fontPx * 0.22);
        ctx.fill();
        ctx.restore();
    }

    ctx.shadowColor = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur = fontPx * 0.08;
    ctx.lineJoin = 'round';
    lines.forEach((ln, i) => {
        const y = (i - (lines.length - 1) / 2) * lh;
        if (t.stroke) { ctx.lineWidth = fontPx * 0.14; ctx.strokeStyle = '#000'; ctx.strokeText(ln, 0, y); }
        ctx.fillStyle = t.color || '#ffffff';
        ctx.fillText(ln, 0, y);
    });
    ctx.restore();
};

// Composite an IMAGE template with an optional user photo + frame + text layers → download JPEG.
// Layer order: base media → photo (back) → frame → photo (front) → texts (top). Throws on CORS taint.
export const downloadComposedImage = async ({ baseUrl, frameUrl, photo, texts, filename }) => {
    const base = await loadImage(baseUrl, true);
    const frame = frameUrl ? await loadImage(frameUrl, true) : null;
    const photoImg = photo && photo.url ? await loadImage(photo.url, false) : null;

    const canvas = document.createElement('canvas');
    canvas.width = base.naturalWidth || REQ_W;
    canvas.height = base.naturalHeight || REQ_H;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(base, 0, 0, canvas.width, canvas.height);
    const drawP = () => photoImg && drawPhotoLayer(ctx, photoImg, photo, canvas.width, canvas.height);
    const drawF = () => frame && ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
    // order: photo "front" sits above the frame; default "back" sits under it
    if (photo && photo.order === 'front') { drawF(); drawP(); } else { drawP(); drawF(); }
    (texts || []).forEach((t) => drawTextLayer(ctx, t, canvas.width, canvas.height));

    const blob = await new Promise((res, rej) =>
        canvas.toBlob((b) => (b ? res(b) : rej(new Error('compose failed'))), 'image/jpeg', 0.92));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
};

// Composite a VIDEO template with an optional user photo + frame + text layers → download WebM (real-time).
export const downloadComposedVideo = async ({ videoUrl, frameUrl, photo, texts, filename }) => {
    if (typeof MediaRecorder === 'undefined') throw new Error('MediaRecorder unsupported');

    const frameImg = frameUrl ? await loadImage(frameUrl, true) : null;
    const photoImg = photo && photo.url ? await loadImage(photo.url, false) : null;

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;
    video.playsInline = true;
    video.muted = true;
    await new Promise((res, rej) => { video.onloadedmetadata = () => res(); video.onerror = () => rej(new Error('video load')); });

    const w = video.videoWidth || REQ_W;
    const h = video.videoHeight || REQ_H;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');

    const stream = canvas.captureStream(30);
    try {
        const vStream = video.captureStream ? video.captureStream() : (video.mozCaptureStream ? video.mozCaptureStream() : null);
        if (vStream) vStream.getAudioTracks().forEach((t) => stream.addTrack(t));
    } catch (e) { /* silent */ }

    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType: mime });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    const stopped = new Promise((res) => { recorder.onstop = () => res(); });

    let raf = 0;
    const draw = () => {
        ctx.drawImage(video, 0, 0, w, h);
        const drawP = () => photoImg && drawPhotoLayer(ctx, photoImg, photo, w, h);
        const drawF = () => frameImg && ctx.drawImage(frameImg, 0, 0, w, h);
        if (photo && photo.order === 'front') { drawF(); drawP(); } else { drawP(); drawF(); }
        (texts || []).forEach((t) => drawTextLayer(ctx, t, w, h));
        raf = requestAnimationFrame(draw);
    };

    recorder.start();
    await video.play();
    draw();

    await new Promise((res) => { video.onended = () => res(); });
    cancelAnimationFrame(raf);
    if (recorder.state !== 'inactive') recorder.stop();
    await stopped;

    const blob = new Blob(chunks, { type: 'video/webm' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
};

// Composite a base image + a frame overlay (both 1080×1920) into one JPEG and download it.
// Throws on canvas taint (CORS) — caller should fall back to layer-by-layer download.
export const downloadImageComposite = async (baseUrl, frameUrl, filename) => {
    const [base, frame] = await Promise.all([loadCorsImage(baseUrl), loadCorsImage(frameUrl)]);
    const canvas = document.createElement('canvas');
    canvas.width = base.naturalWidth || REQ_W;
    canvas.height = base.naturalHeight || REQ_H;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(base, 0, 0, canvas.width, canvas.height);
    ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res, rej) =>
        canvas.toBlob((b) => (b ? res(b) : rej(new Error('compose failed'))), 'image/jpeg', 0.92));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
};

// Composite a video + a frame overlay into a single downloadable video (WebM, real-time).
// Throws on CORS taint / unsupported recorder — caller falls back.
export const downloadVideoComposite = async (videoUrl, frameUrl, filename) => {
    if (typeof MediaRecorder === 'undefined') throw new Error('MediaRecorder unsupported');

    const frameImg = await loadCorsImage(frameUrl);

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;
    video.playsInline = true;
    video.muted = true;
    await new Promise((res, rej) => { video.onloadedmetadata = () => res(); video.onerror = () => rej(new Error('video load')); });

    const w = video.videoWidth || REQ_W;
    const h = video.videoHeight || REQ_H;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');

    const stream = canvas.captureStream(30);
    try {
        const vStream = video.captureStream ? video.captureStream() : (video.mozCaptureStream ? video.mozCaptureStream() : null);
        if (vStream) vStream.getAudioTracks().forEach((t) => stream.addTrack(t));
    } catch (e) { /* silent composite */ }

    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType: mime });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    const stopped = new Promise((res) => { recorder.onstop = () => res(); });

    let raf = 0;
    const draw = () => {
        ctx.drawImage(video, 0, 0, w, h);
        ctx.drawImage(frameImg, 0, 0, w, h);
        raf = requestAnimationFrame(draw);
    };

    recorder.start();
    await video.play();
    draw();

    await new Promise((res) => { video.onended = () => res(); });
    cancelAnimationFrame(raf);
    if (recorder.state !== 'inactive') recorder.stop();
    await stopped;

    const blob = new Blob(chunks, { type: 'video/webm' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
};
